import { Dropdown } from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

type TTimeSelectorProps = {
  label: string;
  value?: number;
  onChange: (time: number) => void;
  min?: number;
  max?: number;
};

const useTimeOptions = (min?: number, max?: number) => {
  const intl = useIntl();

  return useMemo(() => {
    const startOfDay = dayjs().startOf('day');

    // Generate times every 30 minutes (48 slots in a day)
    return Array.from({ length: 48 }, (_, i) => {
      const time = startOfDay.add(i * 30, 'minute');
      const value = time.hour() * 3600 + time.minute() * 60;

      // Return null if option doesn't meet constraints
      if ((min !== undefined && value <= min) || (max !== undefined && value >= max)) {
        return null;
      }

      return {
        value,
        label: intl.formatTime(time.toDate(), {
          hour: 'numeric',
          minute: '2-digit',
        }),
      };
    }).filter(Boolean) as Array<{ label: string; value: number }>;
  }, [intl, min, max]);
};

export const TimeSelector = ({ label, value, onChange, min, max }: TTimeSelectorProps) => {
  const options = useTimeOptions(min, max);

  return (
    <Dropdown
      className="time-selector"
      label={label}
      ariaLabel={label}
      inputValue={value?.toString() || ''}
      onChange={v => onChange(Number(v))}>
      {options.map(option => (
        <Dropdown.Option
          key={option.value}
          value={option.value.toString()}
          content={{ label: option.label }}
        />
      ))}
    </Dropdown>
  );
};
